import { t } from "@lingui/core/macro";
import { createFileRoute, Navigate } from "@tanstack/react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { resolveProductLines } from "@/util/withdrawal";

import { SelectedProductsTable } from "../../-components/Withdrawal/SelectedProductsTable";
import { HelpBlock, Screen, StepFooter, useFlow, useFlowNavigate } from "../../-components/Withdrawal/WithdrawalFlow";
import { NextSteps } from "../../-components/Withdrawal/WithdrawalReview";

const ProductReviewScreen = () => {
  const navigate = useFlowNavigate();
  const { inputs, productLines, shipping } = useFlow();
  const lines = resolveProductLines(productLines, inputs.products);
  const totalUnits = lines.reduce((sum, line) => sum + line.quantity, 0);

  if (!shipping) return <Navigate to="/trade/withdraw/products/shipping" replace />;

  return (
    <Screen subtitle={t`Review your products and delivery details.`} step={{ index: 4, total: 5 }}>
      <div className="grid gap-4 @md:grid-cols-[1.4fr_1fr] @md:items-start">
        <Card className="overflow-hidden shadow-none">
          <CardHeader className="border-b">
            <CardTitle className="text-base">{t`Withdrawal details`}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t`Review your products and delivery details before submitting.`}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border-b bg-muted/30 p-5">
              <p className="text-xs text-muted-foreground">{t`Physical products`}</p>
              <p className="mt-1 font-semibold">
                {lines.length} {lines.length === 1 ? t`product` : t`products`} · {totalUnits}{" "}
                {totalUnits === 1 ? t`unit` : t`units`}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{t`Fulfillment review required`}</p>
            </div>

            <SelectedProductsTable lines={lines} className="p-5" />

            <div className="border-t p-5 text-sm">
              <p className="font-semibold">{t`Shipping address`}</p>
              <p className="mt-1 whitespace-pre-line">{shipping.address}</p>
              {shipping.kind === "new" && (
                <p className="mt-2 text-xs text-muted-foreground">{t`For this withdrawal only.`}</p>
              )}
            </div>
          </CardContent>
        </Card>
        <NextSteps
          items={[
            t`Client Relations emails you a quote for the products you selected.`,
            t`You review and finalize the quote before we ship.`,
            t`Your metals are shipped with insurance, and we send you the tracking information.`,
          ]}
        />
      </div>

      <StepFooter
        onBack={() => navigate("products/shipping")}
        onContinue={() => navigate("products/signature")}
        continueLabel={t`Continue to signature`}
        continueDisabled={lines.length === 0}
      />
      <HelpBlock />
    </Screen>
  );
};

export const Route = createFileRoute("/_authenticated/trade/withdraw/products/review")({
  component: ProductReviewScreen,
});
